import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import {
  TECHNICAL_OBJECT_STATUSES,
  buildLocationTree,
  formatSequenceNumber,
  locationLabel,
  type Beacon,
  type BeaconWriteRequest,
  type LocationNode,
  type LocationTreeNode,
  type TechnicalObjectStatus,
} from '@oscom/shared';
import { getCustomer } from '../api/customers';
import { listLocations } from '../api/locations';
import {
  createBeacon,
  deleteTechnicalObject,
  listTechnicalObjects,
  saveBeacon,
} from '../api/technical-objects';
import { useSession } from '../auth/SessionProvider';
import { CustomerContextNav } from '../components/CustomerContextNav';
import { Layout } from '../components/Layout';
import { NotFoundPage } from './NotFoundPage';
import { TECHNICAL_OBJECT_STATUS_LABELS } from './technicalObjectStatusLabels';

const BEACON_NAME_MAX_LENGTH = 100;

interface LocationOption {
  id: string;
  label: string;
  depth: number;
}

function flattenLocations(entries: readonly LocationTreeNode[], depth = 0): LocationOption[] {
  return entries.flatMap((entry) => [
    { id: entry.node.id, label: locationLabel(entry.node), depth },
    ...flattenLocations(entry.children, depth + 1),
  ]);
}

/** Location names of a stored id path, joined for display; unknown ids are skipped. */
function pathLabel(
  path: readonly string[],
  locationsById: ReadonlyMap<string, LocationNode>,
): string {
  return path
    .map((id) => locationsById.get(id))
    .filter((node): node is LocationNode => node !== undefined)
    .map((node) => locationLabel(node))
    .join(' › ');
}

/** Trimmed value, or `undefined` so an empty field is left out of the request. */
function optional(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

/**
 * Technical objects (beacons) of one customer.
 *
 * Reading needs `objects:read`, every editing element `objects:write`. The page
 * only exists while the `technicalObjects` feature switch is on; the session's
 * `features` is read defensively so an API/web deploy skew shows "not found"
 * instead of crashing.
 */
export function TechnicalObjectsPage(): ReactNode {
  const { session } = useSession();
  const { customerId: customerIdParam } = useParams<{ customerId: string }>();
  const enabled =
    session?.status === 'authenticated' && session.me.features?.technicalObjects === true;

  const [customerName, setCustomerName] = useState<string | undefined>(undefined);
  const [locations, setLocations] = useState<LocationNode[]>([]);
  const [objects, setObjects] = useState<Beacon[] | undefined>(undefined);
  const [loadError, setLoadError] = useState<string | undefined>(undefined);

  const [formMode, setFormMode] = useState<'closed' | 'create' | 'edit'>('closed');
  const [selected, setSelected] = useState<Beacon | undefined>(undefined);
  const [beaconName, setBeaconName] = useState('');
  const [positionDescription, setPositionDescription] = useState('');
  const [mountingNote, setMountingNote] = useState('');
  const [locationId, setLocationId] = useState('');
  const [status, setStatus] = useState<TechnicalObjectStatus>('planned');
  const [serialNumber, setSerialNumber] = useState('');
  const [macAddress, setMacAddress] = useState('');
  const [uuid, setUuid] = useState('');

  const [formError, setFormError] = useState<string | undefined>(undefined);
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadAll = useCallback((id: string, cancelledRef: { current: boolean }) => {
    Promise.all([getCustomer(id), listLocations(id), listTechnicalObjects(id)])
      .then(([customer, locationResponse, objectResponse]) => {
        if (cancelledRef.current) return;
        setCustomerName(customer.name);
        setLocations(locationResponse.locations);
        setObjects(objectResponse.objects);
        setLoadError(undefined);
      })
      .catch((caught: unknown) => {
        if (cancelledRef.current) return;
        setLoadError(
          caught instanceof Error ? caught.message : 'Objekte konnten nicht geladen werden.',
        );
      });
  }, []);

  useEffect(() => {
    if (!customerIdParam || !enabled) return;
    const cancelledRef = { current: false };
    loadAll(customerIdParam, cancelledRef);
    return () => {
      cancelledRef.current = true;
    };
  }, [customerIdParam, enabled, loadAll]);

  if (session?.status !== 'authenticated') return null;
  if (!enabled) return <NotFoundPage />;
  if (!customerIdParam) return null;
  const { me } = session;
  const customerId = customerIdParam;
  const canWrite = me.permissions.includes('objects:write');

  function resetMessages(): void {
    setFormError(undefined);
    setNameError(undefined);
    setConfirmingDelete(false);
  }

  function openCreate(): void {
    setSelected(undefined);
    setFormMode('create');
    setBeaconName('');
    setPositionDescription('');
    setMountingNote('');
    setLocationId('');
    setStatus('planned');
    setSerialNumber('');
    setMacAddress('');
    setUuid('');
    resetMessages();
  }

  function openEdit(object: Beacon): void {
    setSelected(object);
    setFormMode('edit');
    setBeaconName(object.beaconName);
    setPositionDescription(object.positionDescription ?? '');
    setMountingNote(object.mountingNote ?? '');
    setLocationId(object.locationId ?? '');
    setStatus(object.status);
    setSerialNumber(object.hardware?.serialNumber ?? '');
    setMacAddress(object.hardware?.macAddress ?? '');
    setUuid(object.hardware?.uuid ?? '');
    resetMessages();
  }

  function closeForm(): void {
    setFormMode('closed');
    setSelected(undefined);
    resetMessages();
  }

  function reload(): void {
    loadAll(customerId, { current: false });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    resetMessages();
    const name = beaconName.trim();
    // Only the missing-name case is pre-checked; MAC and UUID formats are
    // judged by the API, whose message is the authoritative one.
    if (name === '') {
      setNameError('Bitte geben Sie einen Beacon Name an.');
      return;
    }
    if (name.length > BEACON_NAME_MAX_LENGTH) {
      setNameError(`Der Beacon Name darf höchstens ${BEACON_NAME_MAX_LENGTH} Zeichen lang sein.`);
      return;
    }
    const hardware = {
      ...(optional(serialNumber) ? { serialNumber: optional(serialNumber) } : {}),
      ...(optional(macAddress) ? { macAddress: optional(macAddress) } : {}),
      ...(optional(uuid) ? { uuid: optional(uuid) } : {}),
    };
    const write: BeaconWriteRequest = {
      beaconName: name,
      ...(optional(positionDescription)
        ? { positionDescription: optional(positionDescription) }
        : {}),
      ...(optional(mountingNote) ? { mountingNote: optional(mountingNote) } : {}),
      ...(locationId ? { locationId } : {}),
      status,
      ...(Object.keys(hardware).length > 0 ? { hardware } : {}),
      ...(formMode === 'edit' && selected?._etag ? { ifMatchEtag: selected._etag } : {}),
    };
    setSaving(true);
    try {
      if (formMode === 'edit' && selected) {
        await saveBeacon(customerId, selected.id, write);
      } else {
        await createBeacon(customerId, write);
      }
      closeForm();
      reload();
    } catch (caught: unknown) {
      setFormError(caught instanceof Error ? caught.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(): Promise<void> {
    if (!selected) return;
    setFormError(undefined);
    setDeleting(true);
    try {
      await deleteTechnicalObject(customerId, selected.id);
      closeForm();
      reload();
    } catch (caught: unknown) {
      setConfirmingDelete(false);
      setFormError(caught instanceof Error ? caught.message : 'Löschen fehlgeschlagen.');
    } finally {
      setDeleting(false);
    }
  }

  const locationsById = new Map(locations.map((node) => [node.id, node]));
  const locationOptions = flattenLocations(buildLocationTree(locations));

  return (
    <Layout user={{ displayName: me.displayName, role: me.role }} showLogout>
      <section className="stack">
        {customerName ? (
          <CustomerContextNav
            customerId={customerId}
            customerName={customerName}
            features={me.features}
          />
        ) : null}

        <div className="card">
          <h1>Objekte</h1>
          {canWrite ? (
            <p>
              <button type="button" className="button button--primary" onClick={openCreate}>
                Neues Objekt
              </button>
            </p>
          ) : null}

          {loadError ? (
            <p className="notice notice--warning">{loadError}</p>
          ) : objects === undefined ? (
            <p className="muted" role="status" aria-live="polite">
              Objekte werden geladen ...
            </p>
          ) : objects.length === 0 ? (
            <p className="muted">Noch kein Objekt angelegt.</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Nr.</th>
                    <th scope="col">Beacon Name</th>
                    <th scope="col">Position</th>
                    <th scope="col">Ort</th>
                    <th scope="col">Status</th>
                    <th scope="col">Seriennummer</th>
                    {canWrite ? <th scope="col">Aktion</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {objects.map((object) => (
                    <tr key={object.id}>
                      <td>{formatSequenceNumber(object.sequenceNumber)}</td>
                      <td>{object.beaconName}</td>
                      <td>{object.positionDescription ?? ''}</td>
                      <td>{pathLabel(object.locationPath, locationsById)}</td>
                      <td>{TECHNICAL_OBJECT_STATUS_LABELS[object.status]}</td>
                      <td>{object.hardware?.serialNumber ?? ''}</td>
                      {canWrite ? (
                        <td>
                          <button
                            type="button"
                            className="button button--ghost"
                            onClick={() => openEdit(object)}
                          >
                            Bearbeiten
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {canWrite && formMode !== 'closed' ? (
          <div className="card">
            <h2>
              {formMode === 'edit'
                ? `Objekt ${formatSequenceNumber(selected?.sequenceNumber ?? 0)} bearbeiten`
                : 'Neues Objekt'}
            </h2>
            <form className="stack" noValidate onSubmit={(event) => void handleSubmit(event)}>
              <div className="form-field">
                <label htmlFor="object-beacon-name">Beacon Name</label>
                <input
                  id="object-beacon-name"
                  type="text"
                  maxLength={BEACON_NAME_MAX_LENGTH}
                  aria-required="true"
                  aria-invalid={nameError ? true : undefined}
                  value={beaconName}
                  onChange={(event) => setBeaconName(event.target.value)}
                />
                {nameError ? <p className="notice notice--warning">{nameError}</p> : null}
              </div>

              <div className="form-field">
                <label htmlFor="object-position">Position</label>
                <input
                  id="object-position"
                  type="text"
                  value={positionDescription}
                  onChange={(event) => setPositionDescription(event.target.value)}
                />
              </div>

              <div className="form-field">
                <label htmlFor="object-mounting-note">Montagehinweis</label>
                <textarea
                  id="object-mounting-note"
                  rows={3}
                  value={mountingNote}
                  onChange={(event) => setMountingNote(event.target.value)}
                />
              </div>

              <div className="form-field">
                <label htmlFor="object-location">Ort</label>
                <select
                  id="object-location"
                  value={locationId}
                  onChange={(event) => setLocationId(event.target.value)}
                >
                  <option value="">(kein Ort)</option>
                  {locationOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {'— '.repeat(option.depth)}
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-field">
                <label htmlFor="object-status">Status</label>
                <select
                  id="object-status"
                  value={status}
                  onChange={(event) => setStatus(event.target.value as TechnicalObjectStatus)}
                >
                  {TECHNICAL_OBJECT_STATUSES.map((candidate) => (
                    <option key={candidate} value={candidate}>
                      {TECHNICAL_OBJECT_STATUS_LABELS[candidate]}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-field">
                <label htmlFor="object-serial">Seriennummer</label>
                <input
                  id="object-serial"
                  type="text"
                  value={serialNumber}
                  onChange={(event) => setSerialNumber(event.target.value)}
                />
              </div>

              <div className="form-field">
                <label htmlFor="object-mac">MAC-Adresse</label>
                <input
                  id="object-mac"
                  type="text"
                  placeholder="AA:BB:CC:DD:EE:FF"
                  value={macAddress}
                  onChange={(event) => setMacAddress(event.target.value)}
                />
              </div>

              <div className="form-field">
                <label htmlFor="object-uuid">UUID</label>
                <input
                  id="object-uuid"
                  type="text"
                  value={uuid}
                  onChange={(event) => setUuid(event.target.value)}
                />
              </div>

              {formError ? <p className="notice notice--warning">{formError}</p> : null}

              {confirmingDelete ? (
                <div className="notice notice--warning" role="alertdialog" aria-live="polite">
                  <p>
                    Soll das Objekt „{selected?.beaconName}“ wirklich gelöscht werden? Das kann
                    nicht rückgängig gemacht werden.
                  </p>
                  <button
                    type="button"
                    className="button button--primary"
                    disabled={deleting}
                    onClick={() => void handleDelete()}
                  >
                    {deleting ? 'Löscht ...' : 'Endgültig löschen'}
                  </button>{' '}
                  <button
                    type="button"
                    className="button button--ghost"
                    disabled={deleting}
                    onClick={() => setConfirmingDelete(false)}
                  >
                    Abbrechen
                  </button>
                </div>
              ) : (
                <p>
                  <button className="button button--primary" type="submit" disabled={saving}>
                    {saving ? 'Speichert ...' : 'Speichern'}
                  </button>{' '}
                  <button type="button" className="button button--ghost" onClick={closeForm}>
                    Schließen
                  </button>
                  {formMode === 'edit' ? (
                    <>
                      {' '}
                      <button
                        type="button"
                        className="button button--ghost"
                        onClick={() => setConfirmingDelete(true)}
                      >
                        Löschen
                      </button>
                    </>
                  ) : null}
                </p>
              )}
            </form>
          </div>
        ) : null}
      </section>
    </Layout>
  );
}
